<template>
  <div class="oct-dialog">
    <div class="oct-dialog__container">
      <div class="oct-dialog__surface"
        role="alertdialog"
        aria-modal="true"
      >
        <h2 class="oct-dialog__title">
          <slot name="title" />
        </h2>

        <div class="oct-dialog__tab-bar" v-if="$scopedSlots.tab">
          <slot name="tab" />
        </div>

        <div class="oct-dialog__content">
          <slot />
        </div>

        <div class="oct-dialog__actions">
          <!-- @event 'cancel' -->
          <oct-button
            size="large"
            @click.native="$emit('cancel')"
            v-if="cancelable"
          >
            Cancel
          </oct-button>
          <!-- @event 'submit' -->
          <oct-button
            type="primary"
            size="large"
            @click.native="$emit('submit')"
          >
            OK
          </oct-button>
        </div>
      </div>
    </div>
    <div class="oct-dialog__scrim" @click="$emit('cancel')"></div>
  </div>
</template>

<script>
import OctButton from '@/components/button/OctButton.vue';

export default {
  components: { OctButton },
  name: 'OctDialog',
  props: {
    cancelable: {
      type: Boolean,
      default: true
    }
  },
  methods: {
    /** Open dialog */
    open () {
      document.body.classList.add('oct-dialog--opening')

      this.$nextTick(() => {
        document.body.classList.remove('oct-dialog--opening')
        document.body.classList.add('oct-dialog--open')
      })
    },
    /** Close dialog */
    close () {
      document.body.classList.remove('oct-dialog--opening')
      document.body.classList.add('oct-dialog--closing')

      setTimeout(() => {
        document.body.classList.remove('oct-dialog--closing')
        document.body.classList.remove('oct-dialog--open')
      }, 500)
    }
  },
  mounted () {
    this.open()
  },
  beforeDestroy () {
    this.close()

    document.body.classList.remove('oct-dialog--closing')
    document.body.classList.remove('oct-dialog--open')
  }
}
</script>

<style scoped lang="scss">
@import "../theme/variables";
@import "../grid/variables";
@import "../typography/variables";
@import "../typography/mixins";

.oct-dialog {
  position: fixed;
  top: 0;
  left: 0;
  display: none;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  z-index: 10;

  .oct-dialog--opening,
  .oct-dialog-closing,
  .oct-dialog--open & {
    display: flex;
  }
}

.oct-dialog__container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-around;
  box-sizing: border-box;
  height: 100%;
  transform: scale(0.8);
  opacity: 0;
  pointer-events: none;

  .oct-dialog--open & {
    transform: scale(1);
    opacity: 1;
  }
}

.oct-dialog__surface {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-grow: 0;
  flex-shrink: 0;
  box-sizing: border-box;
  max-width: calc(100vw - oct-rem(40));
  max-height: calc(100% - oct-rem(40));
  min-width: oct-rem(280);
  padding: oct-rem(24);
  background-color: $oct-theme--on-primary;
  border-radius: oct-rem(16);
  overflow-y: auto;
  pointer-events: auto;

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  max-width: calc(100% - 4rem);
  max-height: calc(100% - 4rem);
  padding: oct-rem(32);
}
}

.oct-dialog__tab-bar {
  flex: 0 0 auto;
  align-self: stretch;
}

.oct-dialog__content {
  font-size: oct-rem(17);
  line-height: (28/17);
  letter-spacing: 0;
  text-decoration: inherit;
  text-transform: inherit;
  margin: 0 oct-rem(-24);
  align-self: stretch;
  flex-grow: 1;
  max-width: calc(100% + oct-rem(24*2));
  padding: oct-rem(4) oct-rem(24) oct-rem(4);
  overflow: auto;
  -webkit-overflow-scrolling: touch;

  .oct-dialog__tab-bar + & {
    padding-top: oct-rem(24);
  }

  & > p {
    text-align: center;
    margin: (-7em/17) auto ((-7em+40)/17);
    max-width: oct-rem(384);

    &::v-deep a {
      color: inherit !important;
    }

    &:last-child {
      margin-bottom: (-7em/17);
    }
  }

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  margin: 0 oct-rem(-32);
  max-width: calc(100% + oct-rem(32*2));
  padding: oct-rem(4) oct-rem(32) oct-rem(4);
}
}

.oct-dialog__title {
  font-size: oct-rem(17);
  font-weight: normal;
  line-height: (24/17);
  letter-spacing: -.02em;
  text-align: center;
  margin: (-5em/17) 0 ((-5em+24)/17);
  flex-grow: 0;

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  font-size: oct-rem(21);
  line-height: (28/21);
  letter-spacing: -.01em;
  margin: (-5em/21) 0 ((-5em+32)/21);
}
}

.oct-dialog__actions {
  display: flex;
  justify-content: center;
  padding: oct-rem(28) 0 0;

  .oct-button {
    margin: 0 oct-rem(8);
    min-width: oct-rem(102);
  }
}

.oct-dialog__scrim {
  position: fixed;
  top: 0;
  left: 0;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  background-color: rgba(0,0,0,.32);
  z-index: -1;

  .oct-dialog--opening,
  .oct-dialog--closing,
  .oct-dialog--open & {
    opacity: 1;
  }
}
</style>

<style lang="scss">
.oct-dialog--open {
  overflow: hidden
}
</style>